import React, { useEffect } from "react";
import { Axis, ReferenceLine, XAxis, YAxis } from "interfaces/chart";
import { styled } from "@mui/material/styles";
import { Divider, IconButton } from "@mui/material";
import { ReferenceSetter } from "components/DataExplorationV2/ConfigureMenu/ReferenceLineSection/ReferenceSetter";
import AddCircle from "@mui/icons-material/AddCircle";
import { ReferenceLines } from "components/DataExplorationV2/ConfigureMenu/ReferenceLineSection/ReferenceLines";
import { MenuTooltip } from "components/DataExplorationV2/MenuTooltip";

const MAX_REF_LINES = 10;

export interface ReferenceLineSectionProps {
  /** The status of API fetch */
  loading: boolean;
  error: boolean;
  /** Axes to reference */
  xAxes: XAxis[];
  yAxes: YAxis[];
  minDate: Date | null;
  maxDate: Date | null;
  referenceLines: ReferenceLine[];
  selectedReferenceLine: ReferenceLine | null;
  onChangeSelectedReferenceLine: (referenceLine: ReferenceLine | null) => void;
  onChangeReferenceLineAxis: (axis: Axis | null) => void;
  onChangeReferenceLineValue: (value: number | string | Date | null) => void;
  onAddReferenceLine: () => void;
  onDeleteReferenceLine: (referenceLineId: string) => void;
  onChangeReferenceLineTitle: (
    referenceLine: ReferenceLine,
    title: string | null
  ) => void;
}

/** The X Axis section within the Configuration Menu */
export const ReferenceLineSection = ({
  loading,
  error,
  xAxes,
  yAxes,
  minDate,
  maxDate,
  referenceLines,
  selectedReferenceLine,
  onChangeSelectedReferenceLine,
  onChangeReferenceLineAxis,
  onChangeReferenceLineValue,
  onAddReferenceLine,
  onDeleteReferenceLine,
  onChangeReferenceLineTitle
}: ReferenceLineSectionProps): JSX.Element => {
  useEffect(() => {
    if (selectedReferenceLine == null && referenceLines.length > 0) {
      onChangeSelectedReferenceLine(referenceLines[0]);
    }
  }, [onChangeSelectedReferenceLine, referenceLines, selectedReferenceLine]);

  return (
    <SectionWrapper>
      <CurrentSeriesSubtitle>
        Add reference line
        <MenuTooltip
          title={
            !!referenceLines && referenceLines.length >= MAX_REF_LINES
              ? "10 reference line max"
              : "Add reference line"
          }
        >
          <span>
            <SecondaryButton
              color="secondary"
              disabled={
                !!referenceLines && referenceLines.length >= MAX_REF_LINES
              }
              onClick={onAddReferenceLine}
            >
              <AddCircle />
            </SecondaryButton>
          </span>
        </MenuTooltip>
      </CurrentSeriesSubtitle>

      {referenceLines.length > 0 && (
        <>
          <ReferenceSetter
            loading={loading}
            error={error}
            xAxes={xAxes}
            yAxes={yAxes}
            minDate={minDate}
            maxDate={maxDate}
            selectedReferenceLine={selectedReferenceLine}
            onChangeReferenceLineAxis={onChangeReferenceLineAxis}
            onChangeReferenceLineValue={onChangeReferenceLineValue}
          />

          <StyledDivider />

          <ReferenceLines
            loading={loading}
            error={error}
            referenceLines={referenceLines}
            selectedReferenceLine={selectedReferenceLine}
            onChangeSelectedReferenceLine={onChangeSelectedReferenceLine}
            onDeleteReferenceLine={onDeleteReferenceLine}
            onChangeReferenceLineTitle={onChangeReferenceLineTitle}
          />
        </>
      )}
    </SectionWrapper>
  );
};

const SectionWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
`;

const StyledDivider = styled(Divider)`
  &.MuiDivider-root {
    margin: 0 -${({ theme }) => theme.spacing(2)} -${({ theme }) =>
        theme.spacing(1)};
  }
`;

const CurrentSeriesSubtitle = styled("div")`
  display: flex;
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  font-size: 0.75rem;
  align-items: center;
  justify-content: space-between;
`;

const SecondaryButton = styled(IconButton)`
  width: 20px;
  height: 20px;
  padding: 0 !important;
  .MuiSvgIcon-root {
    transition: all 150ms ease-out;
    &:hover {
      color: #19173f !important;
      opacity: 0.8;
    }
  }
`;
